<template>
  <div class="orcamento-servico-form">
    <!-- Action buttons for existing servico -->
    <div v-if="isEditMode && Number(servicoData.orcamento_servico_id) > 0" class="text-right" style="margin-bottom: 20px;">
      <Button
        v-if="servicoData.orcamento_servico_status == '5'"
        data-testid="orcamento-servico-btn-reativar"
        @click="handleReativar"
        severity="success"
        icon="pi pi-check"
        :label="t('Reativar serviço')" />
      <Button
        v-if="servicoData.orcamento_servico_status != '5'"
        data-testid="orcamento-servico-btn-cancelar"
        @click="handlePreCancelar"
        severity="danger"
        icon="pi pi-ban"
        :label="t('Cancelar serviço')" />
      <Button
        data-testid="orcamento-servico-btn-remover"
        @click="handleRemover"
        severity="danger"
        icon="pi pi-trash"
        :label="t('Excluir serviço')" />
    </div>

    <!-- Main form -->
    <box 
      :title="t('Informações Gerais')" 
      type="primary" 
      elem-id="boxInformacoesGeraisOrcamentoServico" 
      :validation="validateForm">
      
      <!-- Vendedor -->
      <select-list
        v-if="showPermissionFields"
        :label="t('Vendedor')"
        elem-id="orcamento_servico_select_user_id"
        data-testid="orcamento-servico-select-vendedor"
        :itens-select="atendentesOptions"
        v-model="servicoData.orcamento_servico_user_id"
        :placeholder="t('Escolha o usuario')"
        :validation="validateVendedor"
        required />

      <!-- Tipo -->
      <select-list
        v-if="showPermissionFields"
        :label="t('Tipo')"
        elem-id="orcamento_servico_select_tipo"
        data-testid="orcamento-servico-select-tipo"
        :itens-select="tiposOptions"
        v-model="servicoData.orcamento_servico_tipo"
        :placeholder="t('Escolha o tipo')"
        :validation="validateTipo"
        :callback="handleTipoChange"
        required />

      <!-- Serviço -->
      <select-list
        :label="t('Serviço')"
        elem-id="orcamento_servico_select_servico"
        data-testid="orcamento-servico-select-servico"
        :itens-select="servicosOptions"
        v-model="servicoData.servico_id"
        :placeholder="t('Escolha o servico')"
        :validation="validateServico"
        :callback="handleServicoChange"
        required />

      <!-- Valor -->
      <input-text
        :label="t('Valor total')"
        elem-id="orcamento_servico_input_valor"
        data-testid="orcamento-servico-input-valor"
        v-model="servicoData.servico_valor"
        @update:model-value="valorDigitado = true"
        :placeholder="t('Valor total')"
        :validation="validateValor"
        required />

      <!-- Quantidade -->
      <input-text
        :label="t('Quantidade')"
        elem-id="orcamento_servico_input_qtd"
        data-testid="orcamento-servico-input-qtd"
        v-model="servicoData.orcamento_servico_qtd"
        :placeholder="t('Quantidade')"
        :validation="validateQtd"
        required />
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOrcamentoServico } from '../../composables/useOrcamentoServico';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { permissionsService } from '../../services/permissionsService';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import type { OrcamentosServicos_SelectserviciosRow } from '../../contracts/OrcamentosServicos_SelectserviciosRow';

type Id = number | string;

interface SelectOption {
  ID: Id;
  Name: string;
  Valor?: number;
}

interface ServicoData {
  orcamento_servico_id: Id;
  orcamento_id: Id;
  servico_id: Id;
  servico_valor: string;
  orcamento_servico_qtd: string;
  orcamento_servico_user_id: string;
  orcamento_servico_tipo: string;
  orcamento_servico_status: string;
}

type Servico = OrcamentosServicos_SelectserviciosRow;

const props = withDefaults(defineProps<{
  orcamentoFranquiaId?: Id;
}>(), {
  orcamentoFranquiaId: 0
});

const { t } = useI18n();
const {
  fetchOrcamentoServico,
  saveOrcamentoServico,
  removeOrcamentoServico,
  cancelOrcamentoServico,
  reactivateOrcamentoServico,
  checkPreCancelamento,
  fetchServicos,
  getTipos,
  getStatus
} = useOrcamentoServico();
const { confirmAction } = useConfirmAction();

const isEditMode = ref<boolean>(false);
const orcamentoId = ref<Id>(0);
const servicoId = ref<Id>(0);
const servicosComValorPadrao = ref<Record<string, SelectOption>>({});
// servico_valor é o TOTAL da linha: sugere Valor × quantidade até a pessoa digitar um valor
// (ou enquanto não for um registro salvo, cujo valor nunca é sobrescrito).
const valorDigitado = ref<boolean>(false);

const servicoData = ref<ServicoData>({
  orcamento_servico_id: 0,
  orcamento_id: 0,
  servico_id: 0,
  servico_valor: '0',
  orcamento_servico_qtd: '1',
  orcamento_servico_user_id: '',
  orcamento_servico_tipo: '1',
  orcamento_servico_status: '2'
});

const servicos = ref<Servico[]>([]);
const atendentes = ref<SelectOption[]>([]);
const tipos = ref<SelectOption[]>([]);
const status = ref<SelectOption[]>([]);

const showPermissionFields = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente']);
});

const servicosOptions = computed(() => {
  return servicos.value.map(servico => ({
    ID: servico.servico_id,
    Name: `${servico.servicoscategoria_nome} - ${servico.servico_nome}`
  }));
});

const atendentesOptions = computed(() => {
  return atendentes.value;
});

const tiposOptions = computed(() => {
  return tipos.value;
});

const canSave = computed(() => {
  return validateForm().length === 0;
});

const validateVendedor = (): boolean => {
  return Boolean(servicoData.value.orcamento_servico_user_id) &&
         servicoData.value.orcamento_servico_user_id !== '' &&
         servicoData.value.orcamento_servico_user_id !== '0';
};

const validateTipo = (): boolean => {
  return servicoData.value.orcamento_servico_tipo === '1' ||
         servicoData.value.orcamento_servico_tipo === '2';
};

const validateServico = (): boolean => {
  return Boolean(servicoData.value.servico_id) &&
         servicoData.value.servico_id !== '0' &&
         servicoData.value.servico_id !== '';
};

// Same parser as saveServico (Number): parseFloat would accept "250,00" as
// 250 while Number sends NaN -> null on the wire -> backend "servico_valor is required".
const validateValor = (): boolean => {
  const valor = Number(servicoData.value.servico_valor);
  return servicoData.value.servico_valor !== '' && Number.isFinite(valor) && valor >= 0;
};

const validateQtd = (): boolean => {
  const qtd = Number(servicoData.value.orcamento_servico_qtd);
  return Number.isFinite(qtd) && qtd > 0;
};

const validateForm = (): string[] => {
  const errors: string[] = [];

  if (!validateVendedor()) {
    errors.push(t('Você deve escolher um vendedor'));
  }

  if (!validateTipo()) {
    errors.push(t('Você deve escolher um tipo'));
  }

  if (!validateServico()) {
    errors.push(t('Você deve escolher um serviço'));
  }

  if (!validateValor()) {
    errors.push(t('Você deve escolher um valor válido'));
  }

  if (!validateQtd()) {
    errors.push(t('Você deve escolher uma quantidade válida'));
  }

  return errors;
};

const errMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const initializeForNew = async (budgetId: Id): Promise<void> => {
  log('OrcamentoServicoForm', 'initializeForNew', 'Initializing for new servico', budgetId);

  isEditMode.value = false;
  valorDigitado.value = false;
  orcamentoId.value = budgetId;
  servicoId.value = 0;

  servicoData.value = {
    orcamento_servico_id: 0,
    orcamento_id: budgetId,
    servico_id: 0,
    servico_valor: '0',
    orcamento_servico_qtd: '1',
    orcamento_servico_user_id: permissionsService.getUser()?.sub || '',
    orcamento_servico_tipo: '1',
    orcamento_servico_status: '2'
  };

  await loadInitialData();
};

const loadForEdit = async (serviceId: Id): Promise<void> => {
  log('OrcamentoServicoForm', 'loadForEdit', 'Loading servico for edit', serviceId);

  isEditMode.value = true;
  valorDigitado.value = true;
  servicoId.value = serviceId;

  try {
    const data = await fetchOrcamentoServico(Number(serviceId));
    if (!data) {
      throw new Error(t('Erro ao carregar serviço'));
    }
    // The row comes typed (int tipo/status, number valor/qtd); the form keeps
    // STRINGS (the selects and validateTipo compare against '1'/'2'). Without
    // this normalisation every edit was invalid and, now that the modal
    // honours canSave, unsaveable.
    servicoData.value = {
      orcamento_servico_id: data.orcamento_servico_id,
      orcamento_id: data.orcamento_id,
      servico_id: data.servico_id,
      servico_valor: String(data.servico_valor ?? '0'),
      orcamento_servico_qtd: String(data.orcamento_servico_qtd ?? '1'),
      orcamento_servico_user_id: data.orcamento_servico_user_id ?? '',
      orcamento_servico_tipo: String(data.orcamento_servico_tipo ?? '1'),
      orcamento_servico_status: String(data.orcamento_servico_status ?? '2')
    };

    await loadInitialData();
  } catch (error) {
    log('OrcamentoServicoForm', 'loadForEdit', 'Error', error);
    eventBus.emit('globalMessage.error', errMessage(error, t('Erro ao carregar serviço')));
  }
};

const loadInitialData = async (): Promise<void> => {
  log('OrcamentoServicoForm', 'loadInitialData', 'Loading initial data');

  try {
    await loadServicos();
    tipos.value = getTipos();
    status.value = getStatus();
    await loadAtendentes();
  } catch (error) {
    log('OrcamentoServicoForm', 'loadInitialData', 'Error', error);
  }
};

const loadServicos = async (): Promise<void> => {
  try {
    const servicosData = await fetchServicos();
    servicos.value = servicosData;

    servicosComValorPadrao.value = {};
    servicosData.forEach((servico: Servico) => {
      servicosComValorPadrao.value[String(servico.servico_id)] = {
        ID: servico.servico_id,
        Name: `${servico.servicoscategoria_nome} - ${servico.servico_nome}`,
        Valor: servico.servico_valor_padrao
      };
    });
  } catch (error) {
    log('OrcamentoServicoForm', 'loadServicos', 'Error', error);
  }
};

const loadAtendentes = async (): Promise<void> => {
  try {
    const franquiaId = props.orcamentoFranquiaId;
    log('OrcamentoServicoForm', 'loadAtendentes', 'Loading atendentes for franchise', franquiaId);

    if (!franquiaId || franquiaId === 0) {
      atendentes.value = [];
      log('OrcamentoServicoForm', 'loadAtendentes', 'No franchise ID provided, clearing atendentes');
      return;
    }

    const franquiaIdNum = Number(franquiaId);
    if (servicoData.value.orcamento_servico_tipo === '2') {
      atendentes.value = permissionsService.getOperadoresSelectArrayByFranquiaID(franquiaIdNum, servicoData.value.orcamento_servico_user_id || '');
      log('OrcamentoServicoForm', 'loadAtendentes', 'Loaded operadores:', atendentes.value);
    } else {
      atendentes.value = permissionsService.getAtendentesSelectArrayByFranquiaID(franquiaIdNum, servicoData.value.orcamento_servico_user_id || '');
      log('OrcamentoServicoForm', 'loadAtendentes', 'Loaded atendentes:', atendentes.value);
    }

    if (atendentes.value.length > 0) {
      const currentValue = servicoData.value.orcamento_servico_user_id;
      const isValidOption = atendentes.value.some(a => String(a.ID) === String(currentValue));

      if (!currentValue || !isValidOption) {
        servicoData.value.orcamento_servico_user_id = String(atendentes.value[0].ID);
        log('OrcamentoServicoForm', 'loadAtendentes', 'Auto-selected first vendedor:', atendentes.value[0]);
      }
    } else if (!servicoData.value.orcamento_servico_user_id) {
      // No attendant linked to this franchise (or none loaded): the backend
      // fills an empty vendedor with the signed-in user, so mirror that here
      // instead of leaving the save button disabled with nothing to pick.
      servicoData.value.orcamento_servico_user_id = permissionsService.getUser()?.sub || '';
      log('OrcamentoServicoForm', 'loadAtendentes', 'No atendentes; vendedor defaults to the signed-in user');
    }
  } catch (error) {
    log('OrcamentoServicoForm', 'loadAtendentes', 'Error', error);
    atendentes.value = [];
  }
};

const handleTipoChange = (): void => {
  log('OrcamentoServicoForm', 'handleTipoChange', 'Tipo changed', servicoData.value.orcamento_servico_tipo);
  loadAtendentes();
};

const handleServicoChange = (): void => {
  log('OrcamentoServicoForm', 'handleServicoChange', 'Servico changed', servicoData.value.servico_id);

  sugerirValor();
};

const sugerirValor = (): void => {
  if (valorDigitado.value) return;
  const servico = servicosComValorPadrao.value[String(servicoData.value.servico_id)];
  const qtd = Number(servicoData.value.orcamento_servico_qtd);
  if (servico && servico.Valor !== undefined && Number.isFinite(qtd) && qtd > 0) {
    servicoData.value.servico_valor = String(servico.Valor * qtd);
  }
};

watch(() => servicoData.value.orcamento_servico_qtd, sugerirValor);

const saveServico = async (): Promise<boolean> => {
  log('OrcamentoServicoForm', 'saveServico', 'Saving servico', servicoData.value);

  try {
    await saveOrcamentoServico({
      orcamento_id: Number(servicoData.value.orcamento_id),
      servico_id: Number(servicoData.value.servico_id),
      servico_valor: Number(servicoData.value.servico_valor),
      orcamento_servico_id: Number(servicoData.value.orcamento_servico_id) || undefined,
      orcamento_servico_qtd: Number(servicoData.value.orcamento_servico_qtd) || undefined,
      orcamento_servico_user_id: servicoData.value.orcamento_servico_user_id || undefined,
      orcamento_servico_tipo: Number(servicoData.value.orcamento_servico_tipo) || undefined,
      orcamento_servico_status: Number(servicoData.value.orcamento_servico_status) || undefined
    });
    eventBus.emit('orcamentoservico.saved');
    return true;
  } catch (error) {
    log('OrcamentoServicoForm', 'saveServico', 'Error', error);
    throw error;
  }
};

// Excluir não tem volta: só remove depois do sim no diálogo.
const handleRemover = (): void => {
  confirmAction({
    message: t('O serviço será excluído deste orçamento. Esta ação não pode ser desfeita.'),
    header: t('Excluir serviço?'),
    acceptLabel: t('Excluir serviço'),
    rejectLabel: t('Voltar'),
    onAccept: () => { void removerServico(); },
  });
};

const removerServico = async (): Promise<void> => {
  log('OrcamentoServicoForm', 'removerServico', 'Removing servico', servicoData.value.orcamento_servico_id);

  try {
    await removeOrcamentoServico(Number(servicoData.value.orcamento_servico_id));
    eventBus.emit('globalMessage.success', t('Serviço removido com sucesso'));
    eventBus.emit('orcamentoservico.saved');
    // o serviço não existe mais: o modal não fica aberto oferecendo salvá-lo (2026-0099)
    eventBus.emit('modal.orcamento-servico.close');
  } catch (error) {
    log('OrcamentoServicoForm', 'removerServico', 'Error', error);
    eventBus.emit('globalMessage.error', errMessage(error, t('Erro ao remover serviço')));
  }
};

const handleCancelar = async (): Promise<void> => {
  log('OrcamentoServicoForm', 'handleCancelar', 'Canceling servico', servicoData.value.orcamento_servico_id);

  try {
    await cancelOrcamentoServico(Number(servicoData.value.orcamento_servico_id));
    eventBus.emit('globalMessage.success', t('Serviço cancelado com sucesso'));
    eventBus.emit('orcamentoservico.saved');
    // recarrega para o status 5 trocar Cancelar por Reativar, como o handleReativar (2026-0099)
    await loadForEdit(servicoData.value.orcamento_servico_id);
  } catch (error) {
    log('OrcamentoServicoForm', 'handleCancelar', 'Error', error);
    eventBus.emit('globalMessage.error', errMessage(error, t('Erro ao cancelar serviço')));
  }
};

const handlePreCancelar = async (): Promise<void> => {
  log('OrcamentoServicoForm', 'handlePreCancelar', 'Pre-canceling servico', servicoData.value.orcamento_servico_id);

  try {
    // Os avisos dizem o que cai junto (o orçamento, o compromisso); a pessoa os lê antes do sim.
    const avisos = await checkPreCancelamento(Number(servicoData.value.orcamento_servico_id));
    confirmAction({
      message: [t('Deseja cancelar este serviço?'), ...avisos.map((aviso) => t(String(aviso)))].join(' '),
      header: t('Cancelar serviço?'),
      acceptLabel: t('Cancelar serviço'),
      rejectLabel: t('Voltar'),
      onAccept: () => { void handleCancelar(); },
    });
  } catch (error) {
    log('OrcamentoServicoForm', 'handlePreCancelar', 'Error', error);
    eventBus.emit('globalMessage.error', errMessage(error, t('Erro ao verificar cancelamento')));
  }
};

const handleReativar = async (): Promise<void> => {
  log('OrcamentoServicoForm', 'handleReativar', 'Reactivating servico', servicoData.value.orcamento_servico_id);

  try {
    await reactivateOrcamentoServico(Number(servicoData.value.orcamento_servico_id));
    eventBus.emit('globalMessage.success', t('Serviço reativado com sucesso'));
    eventBus.emit('orcamentoservico.saved');
    await loadForEdit(servicoData.value.orcamento_servico_id);
  } catch (error) {
    log('OrcamentoServicoForm', 'handleReativar', 'Error', error);
    eventBus.emit('globalMessage.error', errMessage(error, t('Erro ao reativar serviço')));
  }
};

watch(() => props.orcamentoFranquiaId, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    log('OrcamentoServicoForm', 'watch:orcamentoFranquiaId', 'Franchise changed from', oldValue, 'to', newValue);
    loadAtendentes();
  }
});

// Expose methods
defineExpose({
  initializeForNew,
  loadForEdit,
  saveServico,
  canSave
});
</script>
